import { yupResolver } from '@hookform/resolvers/yup'
import { LineTextColor } from '@qr-front/icons/line-text-color'
import { fieldErrorCls } from '@qr-front/ui/components/InputField'
import {
	TextAreaInput,
	useResizeTextAreaInput,
} from '@qr-front/ui/components/TextareaInput'
import { useAtomValue, useSetAtom } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react'
import { Resolver, useForm } from 'react-hook-form'
import { maxLength } from 'utils/inputChangeUtils'
import * as yup from 'yup'
import { InferType } from 'yup'

import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'

import { useRhcFieldProps } from '../components/RhcField'
import { TContentFormHandle, TQrContentFormSection } from './qrContentFormTypes'
import { useSyncFormSectionValidity } from './useSyncFormSectionValidity'

export const PLAIN_TEXT_FORM_ID = 'plain-text'
export const PLAIN_TEXT_MAX_LENGTH = 500

export const getPlainTextSchema = (t: TTranslationFunction) =>
	yup.object({
		plainText: yup
			.string()
			.required(t('ui__field_validation_errors__generic__required'))
			.test('plain-text-validation', function (value) {
				if (value && value.toString().length >= PLAIN_TEXT_MAX_LENGTH + 1) {
					return this.createError({
						message: t('ui__field_validation_errors__generic__max_length', {
							length: PLAIN_TEXT_MAX_LENGTH,
						}),
					})
				}
				return true
			}),
	})

export type TPlainTextFormField = InferType<
	ReturnType<typeof getPlainTextSchema>
>

export const plainTextAtom = atomWithReset('')

export const resetPlainTextForm = () => {
	getStore().set(plainTextAtom, RESET)
}

export const PlainTextForm = forwardRef<TContentFormHandle>((_, ref) => {
	const [pasteWarningMessage, setPasteWarningMessage] = useState(false)
	const [hidePasteWarningMessage, setHidePasteWarningMessage] = useState(false)

	const t = useTranslations()

	const form = useForm<TPlainTextFormField>({
		defaultValues: {
			plainText: getStore().get(plainTextAtom),
		},
		mode: 'onTouched',
		reValidateMode: 'onChange',
		resolver: yupResolver(
			getPlainTextSchema(t),
		) as unknown as Resolver<TPlainTextFormField>,
		shouldUnregister: false,
	})

	const {
		formState: { isValid },
		trigger,
	} = form

	useSyncFormSectionValidity({ formId: PLAIN_TEXT_FORM_ID, isValid })

	useImperativeHandle(ref, () => ({
		validate: async () => {
			const isValid = await trigger()
			return { isValid }
		},
	}))

	const { inputProps, error } = useRhcFieldProps({
		form,
		path: 'plainText',
		atom: plainTextAtom,
		// onChangeTransform: (event) => maxLength(SUMMARY_MAX_LENGTH)(event),
	})

	const { register, setValue, clearErrors } = form

	const plainText = useAtomValue(plainTextAtom)
	const setPlainText = useSetAtom(plainTextAtom)

	const textAreaRef = useRef<HTMLTextAreaElement | null>(null)

	const { ref: rhfRef, onChange } = register('plainText')

	useImperativeHandle(ref, () => ({
		validate: async () => {
			const isValid = await trigger()
			return { isValid }
		},
	}))

	const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
		event.preventDefault()
		const pastedText = event.clipboardData.getData('text')
		const currentText = getStore().get(plainTextAtom) || ''
		const combined = currentText + pastedText

		if (pastedText.length > PLAIN_TEXT_MAX_LENGTH) {
			clearErrors('plainText')
			const truncated = pastedText.slice(0, PLAIN_TEXT_MAX_LENGTH)
			setValue('plainText', truncated, { shouldValidate: true })
			setPlainText(truncated)
			setPasteWarningMessage(true)
		} else if (combined.length > PLAIN_TEXT_MAX_LENGTH + 1) {
			const truncated = combined.slice(0, PLAIN_TEXT_MAX_LENGTH + 1)
			setValue('plainText', truncated, { shouldValidate: true })
			setPlainText(truncated)
			setPasteWarningMessage(false)
		} else {
			setValue('plainText', combined, { shouldValidate: true })
			setPlainText(combined)
			setPasteWarningMessage(false)
		}
	}

	useEffect(() => {
		if (!pasteWarningMessage) return

		const fadeTimeout = setTimeout(() => {
			setHidePasteWarningMessage(true)
		}, 5000)

		const hideTimeout = setTimeout(() => {
			setPasteWarningMessage(false)
			setHidePasteWarningMessage(false)
		}, 5500)

		return () => {
			clearTimeout(fadeTimeout)
			clearTimeout(hideTimeout)
		}
	}, [pasteWarningMessage])

	useResizeTextAreaInput(textAreaRef, plainText || '', '96px')

	return (
		<form>
			<TextAreaInput
				{...inputProps}
				required
				error={error}
				label={t('generator__step_1__qr_type__plain_text__label')}
				placeholder={t('generator__step_1__qr_type__plain_text__placeholder')}
				className="resize-none"
				onPaste={handlePaste}
				onChange={(event) => {
					setPasteWarningMessage(false)
					setHidePasteWarningMessage(false)
					const adaptedEvent = maxLength(PLAIN_TEXT_MAX_LENGTH + 1)(event)
					setPlainText(event.currentTarget.value)
					onChange(adaptedEvent)
				}}
				ref={(el) => {
					rhfRef(el)
					textAreaRef.current = el
				}}
			/>
			{pasteWarningMessage ? (
				<p
					className={`${fieldErrorCls} transition-opacity duration-500 ${hidePasteWarningMessage ? 'opacity-0' : 'opacity-100'}`}
				>
					{t('ui__field_validation_errors__paste_limit_exceeded')}
				</p>
			) : null}
		</form>
	)
})

PlainTextForm.displayName = 'PlainTextForm'

export const plainTextFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: PLAIN_TEXT_FORM_ID,
	title: t('generator__step_1__qr_type__plain_text__title'),
	description: t('generator__step_1__qr_type__plain_text__description'),
	icon: <LineTextColor />,
	Content: PlainTextForm,
})
